import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import InternalLinks from '../../../components/feature/InternalLinks';

export default function FabrikaFiberAltyapiProjesi() {
  useEffect(() => {
    document.title = 'Fabrika Fiber Altyapı Projesi | Fiber Ek';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Fabrikalar için profesyonel fiber optik altyapı kurulumu. Yüksek hızlı veri iletimi, güvenilir bağlantı ve endüstriyel standartlara uygun çözümler.');
    }

    // Canonical link
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/fabrika-fiber-altyapi-projesi`;
    document.head.appendChild(canonical);

    // Open Graph tags
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = 'Fabrika Fiber Altyapı Projesi | Fiber Ek';
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = 'Fabrikalar için profesyonel fiber optik altyapı kurulumu. Yüksek hızlı veri iletimi, güvenilir bağlantı ve endüstriyel standartlara uygun çözümler.';
    document.head.appendChild(ogDescription);

    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/fabrika-fiber-altyapi-projesi`;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.content = 'website';
    document.head.appendChild(ogType);

    const ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    ogLocale.content = 'tr_TR';
    document.head.appendChild(ogLocale);

    // Service JSON-LD
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Fabrika Fiber Altyapı Projesi",
      "description": "Fabrikalar için profesyonel fiber optik altyapı kurulumu. Yüksek hızlı veri iletimi, güvenilir bağlantı ve endüstriyel standartlara uygun çözümler.",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Fiber Ek",
        "telephone": "+905537165561",
        "url": import.meta.env.VITE_SITE_URL
      },
      "areaServed": {
        "@type": "Place",
        "name": "İstanbul, Kocaeli, Sakarya, Bursa, Tekirdağ, Yalova"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/fabrika-fiber-altyapi-projesi`
    });
    document.head.appendChild(schema);

    return () => {
      document.head.removeChild(canonical);
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
      document.head.removeChild(ogUrl);
      document.head.removeChild(ogType);
      document.head.removeChild(ogLocale);
      document.head.removeChild(schema);
    };
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Hizmetlerimiz', href: '/#hizmetlerimiz' },
    { label: 'Fabrika Fiber Altyapı Projesi' }
  ];

  const faqData = [
    {
      question: "Fabrika fiber altyapı kurulumu ne kadar sürer?",
      answer: "Fabrikanızın büyüklüğüne göre 3-15 gün arası değişmektedir. Gebze OSB'deki projelerimizde ortalama 7 gün içinde tamamlıyoruz."
    },
    {
      question: "OTDR test nedir ve neden gereklidir?",
      answer: "OTDR test fiber kablonun kalitesini ve kayıplarını ölçer. Fabrika altyapısında kesintisiz veri akışı için kritik öneme sahiptir."
    },
    {
      question: "Fusion splicing hizmeti nedir?",
      answer: "Fiber kabloları kayıpsız olarak birleştirme işlemidir. Fabrika içi uzun mesafe bağlantılarında kullanılır."
    },
    {
      question: "Acil fiber arıza durumunda ne yapılır?",
      answer: "7/24 acil müdahale ekibimiz 30 dakika içinde sahada olur ve fiber kablo tamiri gerçekleştirilir."
    },
    {
      question: "Metro ethernet fiber ek avantajları nelerdir?",
      answer: "Yüksek hızlı internet bağlantısı, düşük gecikme süresi ve garantili bant genişliği sağlar."
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/9eaef4f699676bab44e8078d31c1504b.jpg"
              alt="Gebze OSB Fabrika Bölgesi"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-800/70 to-slate-900/80"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="max-w-4xl mx-auto text-center text-white pt-8">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Fabrika Fiber Altyapı Projesi
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Gebze OSB Sanayi Tesisleri İçin Profesyonel Fiber Optik Altyapı Kurulumu
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+905537165561" 
                  className="px-8 py-4 bg-cyan-500 text-white font-bold rounded-full hover:bg-cyan-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  +90 553 716 55 61
                </a>
                <a 
                  href="https://wa.me/905537165561" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Proje Detayları */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Gebze OSB Fiber Altyapı Projesi Detayları
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Gebze Organize Sanayi Bölgesi'nde faaliyet gösteren fabrikalar için özel olarak tasarlanmış fiber optik altyapı kurulum projemiz. Modern sanayi tesislerinin yüksek hızlı internet ve güvenilir iletişim altyapısı ihtiyaçlarını karşılıyoruz.
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Fiber kablo tamiri</strong> ve yeni kurulum hizmetleri</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Fusion splicing hizmeti</strong> ile kayıpsız bağlantı</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>OTDR test hizmeti</strong> ile kalite kontrolü</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>Metro ethernet fiber ek</strong> ile yüksek hızlı bağlantı</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-cyan-500 mr-3 text-xl"></i>
                    <span><strong>7/24 acil fiber arıza</strong> müdahale hizmeti</span>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20fiber%20optic%20installation%20in%20Gebze%20factory%2C%20technician%20working%20with%20fusion%20splicer%20equipment%2C%20industrial%20setting%20with%20organized%20cable%20management%2C%20modern%20manufacturing%20facility%20fiber%20infrastructure&width=800&height=600&seq=gebze-fiber-install&orientation=landscape"
                  alt="Gebze Fabrika Fiber Kurulum"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Hizmet Detayları */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Fabrika Fiber Altyapı Hizmetlerimiz
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-tools-line text-2xl text-cyan-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Fiber Kablo Tamiri</h3>
                <p className="text-gray-600">
                  Gebze OSB fabrikalarında fiber kablo tamiri ve onarım hizmetleri. Kesilen fiber kabloların fusion splicing ile profesyonel tamiri.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-pulse-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">OTDR Test Hizmeti</h3>
                <p className="text-gray-600">
                  Fiber optik kabloların kalitesini ölçen OTDR test hizmeti. Sanayi tesislerinde güvenilir bağlantı için kritik öneme sahip.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-flashlight-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Acil Fiber Arıza</h3>
                <p className="text-gray-600">
                  7/24 acil fiber arıza müdahale hizmeti. Gebze'deki fabrikalarınızda kesintisiz üretim için hızlı çözüm.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-link text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Fiber Optik Sonlandırma</h3>
                <p className="text-gray-600">
                  SC LC konnektör sonlandırma hizmetleri. OM3 OM4 fiber ek işlemleri ile yüksek performanslı bağlantılar.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-building-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Bina Fiber Kablo Tamiri</h3>
                <p className="text-gray-600">
                  Fabrika binalarında fiber kablo tamiri ve altyapı kurulum hizmetleri. Profesyonel kablolama çözümleri.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-global-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Metro Ethernet Fiber Ek</h3>
                <p className="text-gray-600">
                  Metro ethernet fiber ek hizmetleri ile yüksek hızlı internet bağlantısı. Sanayi tesisleri için özel çözümler.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Gebze OSB Hakkında */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Gebze%20Organize%20Sanayi%20Bolgesi%20aerial%20view%2C%20modern%20industrial%20factories%2C%20organized%20industrial%20zone%2C%20manufacturing%20facilities%2C%20Turkish%20industrial%20district%2C%20clean%20organized%20layout&width=800&height=600&seq=gebze-osb-aerial&orientation=landscape"
                  alt="Gebze OSB Görünümü"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Gebze OSB Fiber Optik Altyapısı
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Gebze Organize Sanayi Bölgesi, Türkiye'nin en önemli sanayi merkezlerinden biridir. Bu bölgedeki fabrikaların fiber optik altyapı ihtiyaçlarını en iyi şekilde karşılıyoruz.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Konum Avantajları</h4>
                      <p className="text-gray-600">İstanbul'a yakınlık ve ulaşım kolaylığı</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-factory-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Sanayi Yoğunluğu</h4>
                      <p className="text-gray-600">Binlerce fabrika ve üretim tesisi</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-wireless-charging-line text-cyan-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Teknoloji İhtiyacı</h4>
                      <p className="text-gray-600">Modern üretim için hızlı internet altyapısı</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Sıkça Sorulan Sorular
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h4>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Fabrika Fiber Altyapı Projesi İçin İletişime Geçin
            </h2>
            <p className="text-cyan-100 text-xl mb-8 max-w-2xl mx-auto">
              Gebze OSB'deki fabrikanız için profesyonel fiber optik altyapı kurulumu. Hemen teklif alın.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+905537165561" 
                className="px-8 py-4 bg-white text-cyan-600 font-bold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                +90 553 716 55 61
              </a>
              <a 
                href="https://wa.me/905537165561" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp İletişim
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}